import Drawer from "@mui/material/Drawer";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useAIAssistant } from "./AIAssistantProvider";
import AIChatHeader from "./AIChatHeader";
import AIChatMessages from "./AIChatMessages";
import AIChatInput from "./AIChatInput";
import AIQuickActions from "./AIQuickActions";
import AIErrorMessage from "./AIErrorMessage";
import QuizConfirmationCard from "./quiz/QuizConfirmationCard";
import QuizSuccessCard from "./quiz/QuizSuccessCard";
import PresentationDraftCard from "./quiz/PresentationDraftCard";
import MDButton from "components/MDButton";
import { AI_PANEL_WIDTH_CSS, CONVERSATION_LIMIT_MESSAGE } from "./constants";

const PANEL_BOTTOM_GAP = 24; // px — keeps input clearly above the screen edge

export default function AIAssistantPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const {
    isOpen,
    close,
    messages,
    draft,
    setDraft,
    sendMessage,
    retryMessage,
    sending,
    restoring,
    error,
    clearError,
    routeContext,
    startNewConversation,
    conversationLimitReached,
    copilotUi,
    confirmCreateQuiz,
    editQuizConfiguration,
    confirmPresentationDraft,
    discardPresentationDraft,
    updatePresentationSlide,
    removePresentationSlide,
    searchPresentationLibrary,
    addPresentationLibrarySlide,
    openCreatedQuiz,
    openCreatedQuizResults,
  } = useAIAssistant();

  const handleSend = () => {
    if (conversationLimitReached) return;
    const text = String(draft || "").trim();
    if (!text) return;
    sendMessage(text);
  };

  const handleQuickAction = (prompt) => {
    if (conversationLimitReached) return;
    sendMessage(prompt);
  };

  const handleStarterSelect = (action) => {
    if (!action || conversationLimitReached) return;
    sendMessage(action.prompt || action.label);
  };

  const dynamicOptions = (copilotUi?.optionChips || []).map((opt) => {
    const id = String(opt.id || "");
    let prompt = opt.prompt;
    if (!prompt) {
      if (id === "open_results" || id === "open-results") prompt = "__open_results__";
      else if (id === "start_quiz" || id === "create-quiz") prompt = "Create a quiz";
      else if (
        id === "my_results" ||
        id === "check-quiz" ||
        id === "refresh_results" ||
        id === "retry-results"
      ) {
        prompt = "Analyze my student quiz results again";
      } else if (id === "ask-quizzes") prompt = "Help me with my quizzes and question bank";
      else if (id === "bank:private") prompt = "Use my private question bank";
      else if (id === "use_microlab") prompt = "Use Microlab public bank";
      else if (
        id.startsWith("cat:") ||
        id.startsWith("topic:") ||
        id.startsWith("use_count:") ||
        id.startsWith("guide:") ||
        id.startsWith("presentation_remove_slide:") ||
        id.startsWith("presentation_update_slide:") ||
        id.startsWith("presentation_remove_3d:") ||
        [
          "generate_missing",
          "confirm_save_generated",
          "discard_generated",
          "open_question_bank",
          "open_copilot",
          "continue_quiz",
          "change_difficulty",
          "switch_bank",
          "presentation_confirm",
          "presentation_discard",
          "propose_presentation",
        ].includes(id)
      ) {
        prompt = id;
      } else if (id.startsWith("nav:")) {
        prompt = opt.prompt || opt.label;
      } else {
        prompt = opt.label;
      }
    }
    return { id: opt.id, label: opt.label, prompt };
  });

  const panelHeight = `calc(100vh - ${PANEL_BOTTOM_GAP}px)`;
  const panelHeightDvh = `calc(100dvh - ${PANEL_BOTTOM_GAP}px)`;

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={close}
      variant={isMobile ? "temporary" : "persistent"}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: isOpen && !isMobile ? AI_PANEL_WIDTH_CSS : 0,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: isMobile ? "100%" : AI_PANEL_WIDTH_CSS,
          maxWidth: isMobile ? "100%" : 480,
          boxSizing: "border-box",
          borderLeft: "1px solid rgba(148,163,184,0.28)",
          boxShadow: isOpen ? "-12px 0 40px rgba(15, 23, 42, 0.08)" : "none",
          position: "fixed",
          top: 0,
          right: 0,
          // Leave a clear gap above the bottom of the screen.
          bottom: PANEL_BOTTOM_GAP,
          height: panelHeight,
          maxHeight: panelHeight,
          "@supports (height: 100dvh)": {
            height: panelHeightDvh,
            maxHeight: panelHeightDvh,
          },
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#ffffff",
          borderBottomLeftRadius: 16,
          borderBottomRightRadius: isMobile ? 0 : 16,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          maxHeight: "100%",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <AIChatHeader
            sectionLabel={routeContext.label}
            onClose={close}
            onNewConversation={startNewConversation}
            disabled={sending || restoring}
            conversationLimitReached={conversationLimitReached}
          />
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <AIChatMessages
            messages={messages}
            sending={sending}
            restoring={restoring}
            sectionLabel={routeContext.label}
            onRetry={conversationLimitReached ? undefined : retryMessage}
            onStarterSelect={handleStarterSelect}
          />
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            bgcolor: "#ffffff",
            borderTop: "1px solid rgba(148,163,184,0.18)",
            pb: 1.5,
          }}
        >
          {conversationLimitReached ? (
            <Alert
              severity="warning"
              variant="outlined"
              sx={{
                mx: 2,
                mb: 1,
                borderRadius: 2,
                alignItems: "center",
                "& .MuiAlert-message": { fontSize: 13 },
              }}
              action={
                <MDButton
                  size="small"
                  variant="gradient"
                  color="info"
                  onClick={startNewConversation}
                  disabled={sending || restoring}
                >
                  New conversation
                </MDButton>
              }
            >
              {CONVERSATION_LIMIT_MESSAGE}
            </Alert>
          ) : (
            <AIErrorMessage message={error} onDismiss={clearError} />
          )}
          {copilotUi?.createdQuiz ? (
            <QuizSuccessCard
              quiz={copilotUi.createdQuiz}
              onOpenDetails={openCreatedQuiz}
              onOpenResults={openCreatedQuizResults}
            />
          ) : null}
          {copilotUi?.confirmation ? (
            <QuizConfirmationCard
              preview={copilotUi.confirmation}
              onConfirm={confirmCreateQuiz}
              onEdit={editQuizConfiguration}
              confirming={copilotUi.confirming || sending}
              disabled={sending || restoring || conversationLimitReached}
            />
          ) : null}
          {copilotUi?.pendingPresentation?.slides?.length && !copilotUi?.confirmation ? (
            <PresentationDraftCard
              pending={copilotUi.pendingPresentation}
              onConfirm={confirmPresentationDraft}
              onDiscard={discardPresentationDraft}
              onUpdateSlide={updatePresentationSlide}
              onRemoveSlide={removePresentationSlide}
              onSearchLibrary={searchPresentationLibrary}
              onAddLibrarySlide={addPresentationLibrarySlide}
              confirming={copilotUi.confirming || sending}
              disabled={sending || restoring || conversationLimitReached}
            />
          ) : null}
          <AIQuickActions
            visible={
              dynamicOptions.length > 0 &&
              !copilotUi?.confirmation &&
              !copilotUi?.pendingPresentation?.slides?.length &&
              !conversationLimitReached
            }
            disabled={sending || restoring || copilotUi?.confirming || conversationLimitReached}
            onSelect={handleQuickAction}
            actions={dynamicOptions}
          />
          <AIChatInput
            value={draft}
            onChange={setDraft}
            onSend={handleSend}
            sending={sending}
            disabled={restoring || conversationLimitReached}
            placeholder={
              conversationLimitReached
                ? "Start a new conversation to continue…"
                : undefined
            }
          />
        </Box>
      </Box>
    </Drawer>
  );
}
